import {
  AbstractControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  CultureKeyType,
  DEFAULT_CREDIT_CARD_MASK,
  MAX_INFANT_AGE_YEARS,
  MINOR_AGE_UPPER_CUTOFF,
  MIN_INFANT_AGE_DAYS,
} from '@constants/app.constants';
import { DATE_FORMAT_DEFAULT } from '@constants/date-format.constants';
import {
  DATE_OUT_OF_RANGE_KEY,
  INFANT_DATE_OUT_OF_RANGE_KEY,
  INFANT_INVALID_DATE_KEY,
  INVALID_DATE_KEY, MAX_LENGTH_KEY,
  MIN_AGE_CONTACT_DATA_COLLECTION, REQUIRED_LENGTH_KEY,
} from '@constants/form.constants';
import { NullableString } from '@models/app.models';
import { N0Journey, PassengerTypeCode } from '@models/booking.models';
import { AppCopy } from '@models/cms/app.model';
import {
  PaymentForm,
  TripDetailsPassengerFormConfig,
} from '@models/form/form.models';
import { Station } from '@models/responses/station-response.model';
import { DateTime } from 'luxon';
import {
  dateWithTimeToDateWithoutTime,
  getPaymentMethodCodeFromCardNumber,
  PAYMENT_METHOD_REGEX,
} from './data-utils';
import { gt, gte, lt, lte } from './func-utils';
import { getTypeInfo } from 'credit-card-type';

export const triggerDisplayFormErrors = (
  groupArrayOrControl:
    | UntypedFormGroup
    | UntypedFormArray
    | UntypedFormControl
    | AbstractControl,
): void => {
  groupArrayOrControl.markAllAsTouched();
  groupArrayOrControl.updateValueAndValidity();

  if (groupArrayOrControl instanceof UntypedFormControl) {
    return;
  }

  if (groupArrayOrControl instanceof UntypedFormGroup) {
    Object.values(groupArrayOrControl.controls).forEach((ctrl) =>
      triggerDisplayFormErrors(ctrl),
    );
  } else if (groupArrayOrControl instanceof UntypedFormArray) {
    groupArrayOrControl.controls.forEach((ctrl) =>
      triggerDisplayFormErrors(ctrl),
    );
  } else {
    console.error('Type of form object not supported:', groupArrayOrControl);
  }
};

export const noDuplicatesValidator =
  (ctrlName: string): ValidatorFn =>
  (ctrl: AbstractControl): ValidationErrors | null => {
    const formArray = ctrl as UntypedFormArray;
    const data = formArray.value as { [ctrlName: string]: unknown }[];
    const duplicateData = data.find((datum) => {
      return data.find((datum2) => datum2[ctrlName] === datum[ctrlName]);
    });
    if (!duplicateData) {
      return null;
    }
    return {
      duplicate: {
        [ctrlName]: duplicateData,
      },
    };
  };

export const doShowPassengerEmailAndPhone = (
  form: UntypedFormGroup | null,
  dateFormat: string,
  culture?: CultureKeyType,
): boolean => {
  if (!form) {
    return true;
  }

  const birthDate = form.get('birthDate')?.value as string | undefined;
  if (!birthDate) {
    return true;
  }

  const birthDateParsed = DateTime.fromFormat(birthDate, dateFormat, {
    locale: culture,
  }).toJSDate();

  if (isNaN(birthDateParsed.getTime())) {
    return true;
  }

  const maxBirthDate = dateWithTimeToDateWithoutTime(
    new Date(),
    -MIN_AGE_CONTACT_DATA_COLLECTION,
  );

  const birthDateDateDayOnly = dateWithTimeToDateWithoutTime(birthDateParsed);
  const doShow = birthDateDateDayOnly.getTime() <= maxBirthDate.getTime();

  if (!doShow) {
    form.get('email')?.setValue('');
    form.get('email')?.setErrors(null);

    form.get('phoneCode')?.setValue('');
    form.get('phoneCode')?.setErrors(null);

    form.get('phone')?.setValue('');
    form.get('phone')?.setErrors(null);
  }

  return doShow;
};

export const isNotSet = (value: unknown): boolean => {
  return value === '' || value === null || value === undefined;
};

export const doesStationCodeMatchName = (
  name: string,
  code: string,
  stations: Station[],
): boolean => {
  return stations.some((station) => {
    return station.stationCode === code && station.fullName === name;
  });
};

export const datesComparisonValidator =
  (): ValidatorFn =>
  (c: AbstractControl): ValidationErrors | null => {
    const departDate = <Date>c.get('departDate')?.value;
    const returnDate = <Date>c.get('returnDate')?.value;
    //safety check
    if (!departDate || !returnDate) {
      return null;
    }

    if (returnDate < departDate) {
      return { invalidDates: true };
    }

    return null;
  };

export const isValidStationPairValidator =
  (stations: Station[]): ValidatorFn =>
  (ctrl: AbstractControl): ValidationErrors | null => {
    const group = ctrl as UntypedFormGroup;
    const originCode = group.get('originStationCode')?.value as string;
    const destinationCode = group.get('destinationStationCode')
      ?.value as string;
    const originName = group.get('originStationName')?.value as string;
    const destinationName = group.get('destinationStationName')
      ?.value as string;

    if (!doesStationCodeMatchName(originName, originCode, stations)) {
      return {
        invalidStationName: {
          code: originCode,
          name: originName,
          // expectedName: null,
        },
      };
    }

    if (!doesStationCodeMatchName(destinationName, destinationCode, stations)) {
      return {
        invalidStationName: {
          code: destinationCode,
          name: destinationName,
          // expectedName: null,
        },
      };
    }

    const isValid = stations.some((station) => {
      return (
        station.stationCode === originCode &&
        station.validMarkets?.some(
          (market) => market.stationCode === destinationCode,
        )
      );
    });

    if (!isValid) {
      return {
        invalidStationPair: {
          originCode,
          destinationCode,
          originName,
          destinationName,
        },
      };
    }
    return null;
  };

  export function creditCardLengthValidator(maxLength: number) {
    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }
      const digitsOnly = removeSpacesFromInput(control.value);
      return digitsOnly.length > maxLength
        ? { 'maxLength': { value: control.value } }
        : null;
    };
  }

export const noOnlySpacesValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  return !!control.value && typeof control.value === 'string' && control.value.trim() === '' ? {pattern: true} : null;
}

const testIfOnlySpecialChars = (control: AbstractControl): boolean => {
  return typeof control.value === 'string' && /^[\W_]+$/.test(control.value)
}

const testIfOnlyDigitsAndSpecialChars = (control: AbstractControl): boolean => {
  return typeof control.value === 'string' && /^[\d\W_]+$/.test(control.value)
}

export const noOnlyDigitsAndSpecialCharsValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  return testIfOnlyDigitsAndSpecialChars(control)
    ? {pattern: true}
    : null;
};

export const onlyDigitsAndSpecialCharsValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  return testIfOnlyDigitsAndSpecialChars(control) && !testIfOnlySpecialChars(control)
    ? null
    : { pattern: true };
};

export const isValidPaymentValidator = (
  ctrl: AbstractControl,
): ValidationErrors | null => {
  if ((ctrl.value as string).length < 13) {
    return null;
  }

  const paymentMethodCode = getPaymentMethodCodeFromCardNumber(
    ctrl.value ? removeSpacesFromInput(String(ctrl.value)) : '',
    PAYMENT_METHOD_REGEX,
  );

  if (!paymentMethodCode) {
    return {
      invalidPaymentMethod: {},
    };
  }

  switch (paymentMethodCode) {
    case 'UA':
    case 'Maestro':
      return {
        invalidPaymentMethod: {
          [paymentMethodCode]: {},
        },
      };
  }

  return null;
};

export const mutuallyExclusiveRequiredValidator =
  (ctrlsA: string[], ctrlsB: string[]): ValidatorFn =>
  (ctrl: AbstractControl): ValidationErrors | null => {
    const isEveryCtrlAValueFalsy = ctrlsA.every((ctrlName) => {
      return !ctrl.get(ctrlName)?.value;
    });

    const isEveryCtrlBValueFalsy = ctrlsB.some((ctrlName) => {
      return !ctrl.get(ctrlName)?.value;
    });

    if (isEveryCtrlAValueFalsy && isEveryCtrlBValueFalsy) {
      return {
        mutuallyExclusive: true,
      };
    }

    return null;
  };

export const addressSelectedValidator =
  (): ValidatorFn =>
  (ctrl: AbstractControl): ValidationErrors | null => {
    const addressLine1Valid = !!(<FormGroup<TripDetailsPassengerFormConfig>>(
      ctrl.parent
    ))?.controls?.addressLine1.valid;
    const cityCtrlValid = !!(<FormGroup<TripDetailsPassengerFormConfig>>(
      ctrl.parent
    ))?.controls?.city.valid;
    const provinceStateCtrlValid = !!(<
      FormGroup<TripDetailsPassengerFormConfig>
    >ctrl.parent)?.controls?.provinceState.valid;
    const postalCodeCtrlValid = !!(<FormGroup<TripDetailsPassengerFormConfig>>(
      ctrl.parent
    ))?.controls?.postalCode.valid;
    if (
      !addressLine1Valid ||
      !cityCtrlValid ||
      !provinceStateCtrlValid ||
      !postalCodeCtrlValid
    ) {
      return {
        invalidAddress: true,
      };
    }

    return null;
  };

export const requiredIfValidator =
  (requiredIfControlName: string, requireControlNames: string[]): ValidatorFn =>
  (ctrl: AbstractControl): ValidationErrors | null => {
    const requiredIfControl = ctrl.get(requiredIfControlName);
    if (!requiredIfControl) {
      return null;
    }

    let hasAnyEmptyRequiredControls = false;
    requireControlNames.forEach((name) => {
      const requiredControl = ctrl.get(name);
      if (requiredIfControl.value && !requiredControl?.value) {
        hasAnyEmptyRequiredControls = true;
        requiredControl?.setErrors({
          ...requiredControl.errors,
          required: true,
        });
        return;
      }
      requiredControl?.setErrors(
        requiredControl.errors &&
          Object.keys(requiredControl.errors).some((k) => k !== 'required')
          ? {
              ...requiredControl.errors,
              required: null,
            }
          : null,
      );
    });

    if (hasAnyEmptyRequiredControls) {
      return {
        required: true,
      };
    }

    return null;
  };

export const atLeastOneAdultValidator = (paxKeys: string[]) => {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    const invalidKeys = paxKeys.filter((key) => {
      const formGroup = ctrl.get(key) as UntypedFormGroup;
      const birthDate = formGroup.get('birthDate')?.value as string | undefined;
      if (!birthDate) {
        return false;
      }

      const birthDateParsed = DateTime.fromFormat(
        birthDate,
        DATE_FORMAT_DEFAULT,
      ).toJSDate();

      if (isNaN(birthDateParsed.getTime())) {
        return false;
      }

      const latestBirthDate = dateWithTimeToDateWithoutTime(
        new Date(),
        -MIN_AGE_CONTACT_DATA_COLLECTION,
      );

      const birthDateDateDayOnly =
        dateWithTimeToDateWithoutTime(birthDateParsed);
      return birthDateDateDayOnly.getTime() > latestBirthDate.getTime();
    });

    const areNoValidAdultOnBookingByBirthdate =
      invalidKeys.length >= paxKeys.length;
    if (areNoValidAdultOnBookingByBirthdate) {
      return {
        notEnoughAdults: invalidKeys,
      };
    }

    return null;
  };
};

export const travelerContactValidator = (): ValidatorFn => {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const travelerOneIsContactValue = ctrl.get('travelerOneIsContact')?.value;
      if(travelerOneIsContactValue) {
        if(typeof travelerOneIsContactValue !== "boolean") {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ctrl.get('travelerOneIsContact')!.setValue(true);
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ctrl.get('travelerOneIsContact')!.updateValueAndValidity();
        }
      }
      if (!(travelerOneIsContactValue as boolean)) {
        const firstName = ctrl.get('firstName')?.value as string;
        const lastName = ctrl.get('lastName')?.value as string;
        const email = ctrl.get('email')?.value as string;
        const phoneCode = ctrl.get('phoneCode')?.value as string;
        const phone = ctrl.get('phone')?.value as string;
        if (!firstName || !lastName || !email || !phoneCode || !phone) {
          return ({travelerContactMissing: true});
        }
      }
    }
    return null;
  };
};

export const passengersSameNameValidator = (paxKeys: string[]): ValidatorFn => {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    const names: string[] = [];
    paxKeys.forEach(pk => {
      const formGroup = ctrl.get(pk) as UntypedFormGroup;
      const firstName = formGroup.get('firstName')?.value as string;
      const lastName = formGroup.get('lastName')?.value as string;
      if (firstName && lastName) {
        names.push(firstName.trim() + lastName.trim())
      }
    })

    if (names.length > 1 && new Set(names).size !== names.length) {
      return ({multipleTravelerSameNames: true});
    }

    return null;
  };
};

export const isInListValidator =
  <T>(list: T[], required = false): ValidatorFn =>
  (ctrl: AbstractControl): ValidationErrors | null => {
    if (isNotSet(ctrl.value) && !required) {
      return null;
    }

    const strValue = String(ctrl.value);
    const strList = list.map(String);
    if (strList.includes(strValue)) {
      return null;
    }

    return { forbiddenName: { value: String(ctrl.value) } };
  };

export const checkIsValidDate = (
  day: number,
  month: number | string,
  year: number,
): boolean => {
  // if any date values change on parsing, then the date
  // was not valid.
  const testDate = new Date(Number(year), Number(month), Number(day));
  return (
    testDate.getFullYear() === year &&
    month === testDate.getMonth() &&
    day === testDate.getDate() &&
    !isNaN(testDate.getTime()) // invalid date
  );
};

export const formsTotalValidator =
  ({
    totalControlName,
    expectedTotal,
  }: {
    totalControlName: string;
    expectedTotal: number;
  }): ValidatorFn =>
  (ctrl: UntypedFormArray): ValidationErrors | null => {
    // Number(toFixed) deals with rounding issues, for example if the total is 400.67
    // and one is 200, the other input is 200.67, 200.67 is represented in binary as the
    // decimal equivalent of 200.699999..., so this needs to be rounded up to the first
    // two digits. It's strange and may need more thought in the future.
    const total = Number(
      Object.values(<number>ctrl.value)
        .reduce<number>((total, formGroupValues: PaymentForm) => {
          return total + Number(formGroupValues[totalControlName]);
        }, 0)
        .toFixed(2),
    );
    return total === expectedTotal
      ? null
      : {
          total: {
            expectedTotal,
          },
        };
  };

export const dayValidator =
  ({
    name,
    dayFieldName,
    monthFieldName,
    yearFieldName,
    comparator,
    required = false,
  }: {
    name: string;
    dayFieldName?: string;
    monthFieldName: string;
    yearFieldName: string;
    comparator: (x: number, y: number) => boolean;
    required?: boolean;
  }): ValidatorFn =>
  (ctrl: AbstractControl): ValidationErrors | null => {
    const dayCtrl = dayFieldName ? ctrl.get(dayFieldName) : null;
    const monthCtrl = ctrl.get(monthFieldName);
    const yearCtrl = ctrl.get(yearFieldName);
    if (!yearCtrl || !monthCtrl || (dayFieldName && !dayCtrl)) {
      return null;
    }
    if (
      isNotSet(yearCtrl.value) &&
      isNotSet(monthCtrl.value) &&
      isNotSet(dayCtrl?.value) &&
      !required
    ) {
      return null;
    }
    const yearNum = Number(yearCtrl.value);
    const monthValue = Number(monthCtrl.value);
    // default day to last day if no day control provided
    const dayNum = Number(
      dayCtrl?.value ??
        DateTime.fromObject({ year: yearNum, month: monthValue + 1 })
          .daysInMonth,
    );
    if (!checkIsValidDate(dayNum, monthValue, yearNum)) {
      // one of the values rolled over, this was
      // not a valid date.
      return {
        [`${name}_${INVALID_DATE_KEY}`]: {
          day: dayNum,
          month: monthValue,
          year: yearNum,
        },
      };
    }
    const date = new Date(yearNum, monthValue, dayNum);
    const today = dateWithTimeToDateWithoutTime(new Date());
    return !comparator(date.getTime(), today.getTime())
      ? {
          [`${name}_${DATE_OUT_OF_RANGE_KEY}`]: date,
        }
      : null;
  };

export const passengerTypeValidator =
  ({
    passengerTypeCode,
    departureDate,
    culture,
  }: {
    passengerTypeCode: PassengerTypeCode;
    departureDate: Date;
    culture: string;
  }): ValidatorFn =>
  (ctrl: AbstractControl): ValidationErrors | null => {
    if (isNotSet(ctrl.value)) {
      return null;
    }

    const ctrlDate =
      ctrl.value instanceof Date
        ? ctrl.value
        : DateTime.fromFormat(<string>ctrl.value, DATE_FORMAT_DEFAULT, {
            locale: culture,
          }).toJSDate();

    const enteredDateDayOnly = dateWithTimeToDateWithoutTime(ctrlDate);

    const departureDayOnly = dateWithTimeToDateWithoutTime(departureDate);

    // 18 years ago (less one day), max dob for child (I think?)
    const childMinDob = dateWithTimeToDateWithoutTime(
      departureDayOnly,
      -MINOR_AGE_UPPER_CUTOFF,
      0,
      -1,
    );
    // 18 years ago to the date
    const adultMaxDob = dateWithTimeToDateWithoutTime(
      departureDayOnly,
      -MINOR_AGE_UPPER_CUTOFF,
    );

    const infantMaxDob = dateWithTimeToDateWithoutTime(
      departureDayOnly,
      0,
      0,
      -MIN_INFANT_AGE_DAYS,
    );

    const infantMinDob = dateWithTimeToDateWithoutTime(
      departureDayOnly,
      -MAX_INFANT_AGE_YEARS,
    );

    if (
      passengerTypeCode === PassengerTypeCode.ADT &&
      !lte(enteredDateDayOnly.getTime(), adultMaxDob.getTime())
    ) {
      return {
        [DATE_OUT_OF_RANGE_KEY]: {
          minDate: adultMaxDob,
        },
      };
    }

    if (
      passengerTypeCode === PassengerTypeCode.CHD &&
      !gte(enteredDateDayOnly.getTime(), childMinDob.getTime())
    ) {
      return {
        [DATE_OUT_OF_RANGE_KEY]: {
          maxDate: childMinDob,
        },
      };
    }

    if (
      passengerTypeCode === PassengerTypeCode.INF &&
      (!gte(enteredDateDayOnly.getTime(), infantMinDob.getTime()) ||
        !lte(enteredDateDayOnly.getTime(), infantMaxDob.getTime()))
    ) {
      return {
        [DATE_OUT_OF_RANGE_KEY]: {
          minDate: infantMinDob,
          maxDate: infantMaxDob,
        },
      };
    }

    return null;
  };

export const dateValidator =
  ({
    comparator,
    nameOfControlToCompareTo,
    dateToCompareTo,
    culture,
  }: {
    comparator: (x: number, y: number) => boolean;
    nameOfControlToCompareTo?: string;
    dateToCompareTo?: Date;
    culture?: NullableString;
  }): ValidatorFn =>
  (ctrl: AbstractControl): ValidationErrors | null => {
    if (isNotSet(ctrl.value)) {
      return null;
    }

    const ctrlDate =
      ctrl.value instanceof Date
        ? ctrl.value
        : DateTime.fromFormat(<string>ctrl.value, DATE_FORMAT_DEFAULT, {
            locale: culture ?? undefined,
          }).toJSDate();

    // valueOf is NaN when a date is invalid
    if (isNaN(ctrlDate.getTime())) {
      return {
        [INVALID_DATE_KEY]: {
          ctrlDate,
        },
      };
    }

    if (nameOfControlToCompareTo && dateToCompareTo) {
      console.error(
        `You must supply *either* a date to compare to, or a form control to compare to: not both (ctrl: '${nameOfControlToCompareTo}', date: ${dateToCompareTo}).`,
      );
      return null;
    }

    const compareControl = ctrl.parent?.get(nameOfControlToCompareTo ?? '');

    if (nameOfControlToCompareTo && !compareControl) {
      console.error(
        `The control we are comparing to does not exist (${nameOfControlToCompareTo}).`,
      );
      return null;
    }

    let compareControlValue: Date | null = null;
    if (compareControl?.value instanceof Date) {
      compareControlValue = compareControl.value;
    } else if (typeof compareControl?.value === 'string') {
      compareControlValue = DateTime.fromFormat(
        compareControl.value,
        DATE_FORMAT_DEFAULT,
        {
          locale: culture ?? undefined,
        },
      ).toJSDate();
    }

    if (
      nameOfControlToCompareTo &&
      (!compareControlValue || isNaN(compareControlValue.getTime()))
    ) {
      console.error(
        `The control we are comparing to (${nameOfControlToCompareTo}) does not have a date or date string with the format '${DATE_FORMAT_DEFAULT}' as its value. The raw value is ${compareControl?.value}`,
      );
      return null;
    }

    // set default value of date but not before we check initial values to log errors above
    if (!dateToCompareTo) {
      dateToCompareTo = new Date();
    }

    const compareDate = dateWithTimeToDateWithoutTime(
      compareControlValue ?? dateToCompareTo,
    );

    const controlDateDayOnly = dateWithTimeToDateWithoutTime(ctrlDate);
    const compareDateDayOnly = dateWithTimeToDateWithoutTime(compareDate);

    const isValid = comparator(
      controlDateDayOnly.getTime(),
      compareDateDayOnly.getTime(),
    );

    if (isValid) {
      return null;
    }

    if (comparator === gte || comparator === gt) {
      return {
        [DATE_OUT_OF_RANGE_KEY]: {
          minDate: compareDateDayOnly,
        },
      };
    }

    if (comparator === lte || comparator === lt) {
      return {
        [DATE_OUT_OF_RANGE_KEY]: {
          maxDate: compareDateDayOnly,
        },
      };
    }

    return {
      [DATE_OUT_OF_RANGE_KEY]: ctrlDate,
    };
  };

export const threeFieldsValidator =
  ({
    passKey,
    nameOfDayControl,
    nameOfMonthControl,
    nameOfYearControl,
    infant,
    lastJourneyDepartDate
  }: {
    passKey: string;
    nameOfDayControl: string;
    nameOfMonthControl: string;
    nameOfYearControl: string;
    infant?: boolean;
    lastJourneyDepartDate?: Date;
  }): ValidatorFn =>
  (ctrl: AbstractControl): ValidationErrors | null => {
    const parent = ctrl.parent;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const day = parent?.controls[passKey].controls[nameOfDayControl].value;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const month = parent?.controls[passKey].controls[nameOfMonthControl].value;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const year = parent?.controls[passKey].controls[nameOfYearControl].value;

    // if not all fields are filled
    if (!(day && month && year)) {
      return {
        [infant ? INFANT_INVALID_DATE_KEY : INVALID_DATE_KEY]: {
          ctrlDate: 'Invalid Date',
        },
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const fieldsDate = new Date(Date.UTC(year, month - 1, day));
    // valueOf is NaN when a date is invalid
    if (isNaN(fieldsDate.getTime())) {
      return {
        [INVALID_DATE_KEY]: {
          ctrlDate: fieldsDate,
        },
      };
    }

    // when a date input and output are different
    if (
      !(
        fieldsDate.getUTCFullYear() == year &&
        fieldsDate.getUTCMonth() + 1 == month &&
        fieldsDate.getUTCDate() == day
      )
    ) {
      return {
        [infant ? INFANT_INVALID_DATE_KEY : INVALID_DATE_KEY]: {
          ctrlDate: 'Invalid Date',
        },
      };
    }

    const today = new Date();
    if (fieldsDate > today) {
      return {
        [infant ? INFANT_DATE_OUT_OF_RANGE_KEY : DATE_OUT_OF_RANGE_KEY]: {
          maxDate: today,
        },
      };
    }

    if (infant && lastJourneyDepartDate) {
      const minBirthDateForInfant = new Date(lastJourneyDepartDate);
      minBirthDateForInfant.setFullYear(
        minBirthDateForInfant.getFullYear() - MAX_INFANT_AGE_YEARS,
      );
      if (fieldsDate < minBirthDateForInfant) {
        return {
          [INFANT_DATE_OUT_OF_RANGE_KEY]: {
            minDate: minBirthDateForInfant,
          },
        };
      }
    }

    return null;
  };

export const mustMatchValidator = (
  matchTo: string,
  reverse?: boolean,
): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.parent && reverse) {
      const c = (control.parent?.controls as AbstractControl[])[
        matchTo
      ] as AbstractControl;

      if (c) {
        c.updateValueAndValidity();
      }

      return null;
    }

    return !!control.parent?.value &&
      control.value ===
        (
          (control.parent?.controls as AbstractControl[])[
            matchTo
          ] as AbstractControl
        ).value
      ? null
      : {
          mustMatch: {
            mustMatch: matchTo
              .replace(/([^A-Z])([A-Z])/g, '$1 $2')
              .toLowerCase(),
          },
        };
  };
};

export const getPassportExpiryMinDate = (
  journeys: N0Journey[],
  passportCountryIssuedBy: string,
  destinationCountry: string,
  appCopy: AppCopy | null,
): Date => {
  const passportRule =
    appCopy?.passportRules.find(
      (r) =>
        r.passportCountry === passportCountryIssuedBy &&
        r.destinationCountry === destinationCountry,
    ) ?? appCopy?.passportRules.find((r) => r.passportCountry === '*');
  return journeys[journeys.length - 1]?.designator?.arrival
    ? DateTime.fromJSDate(
        dateWithTimeToDateWithoutTime(
          new Date(journeys[journeys.length - 1]!.designator.arrival), // eslint-disable-line @typescript-eslint/no-non-null-assertion
        ),
      )
        .plus({ days: passportRule?.days })
        .toJSDate()
    : DateTime.fromJSDate(dateWithTimeToDateWithoutTime(new Date()))
        .plus({ month: 6 })
        .toJSDate();

};

export const removeSpacesFromInput = (input: string) => {
  return input.replace(/\s+/g, '');
}

export const getCreditCardNumberMask = (cardNumber: string, cardType: string) => {
  const card = getTypeInfo(cardType);
  if (!card) {
    return DEFAULT_CREDIT_CARD_MASK;
  }
  const appropriateLength = card.lengths
    .find(length => length >= cardNumber.length) || card.lengths[0];
  if (!appropriateLength) {
    return DEFAULT_CREDIT_CARD_MASK;
  }
  const fullMaskedNumber = '0'.repeat(appropriateLength);
  const offsets = [0, ...card.gaps, appropriateLength];
  const components: string[] = [];
  for (let i = 0; i < offsets.length - 1; i++) {
    const start = offsets[i];
    if (start === undefined || offsets[i + 1] === undefined) {
      continue;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const end = Math.min(offsets[i + 1]!, appropriateLength);
    components.push(fullMaskedNumber.substring(start, end));
  }
  return components.join(" ");
}


export function maxLengthValidator(maxLength: number) {
  return (control: AbstractControl) => {
    const value = control.value || '';
    return value.length > maxLength
      ? {
          [MAX_LENGTH_KEY]: {
            [REQUIRED_LENGTH_KEY]: maxLength,
          }
      }
      : null;
  };
}

export function atLeastTwoWordsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    if (typeof control.value !== 'string') {
      return { pattern: true };
    }

    const words = control.value.trim().split(/\s+/);
    return words.length >= 2 ? null : { pattern: true };
  };
}
